import { useRef, useState, useCallback, useEffect } from "react";

// Grabador de una sola pulsación: toggle() arranca / termina.
// Expone estado, segundos, nivel (0..1 para la onda), blob y url.
export default function useGrabador() {
  const [estado, setEstado] = useState("idle"); // idle | grabando | listo
  const [segundos, setSegundos] = useState(0);
  const [nivel, setNivel] = useState(0);
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);

  const mrRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const acRef = useRef(null);
  const arrancandoRef = useRef(false);
  const canceladoRef = useRef(false);

  const limpiarMedios = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (acRef.current) { try { acRef.current.close(); } catch {} acRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  const start = useCallback(async () => {
    if (arrancandoRef.current || estado === "grabando") return;
    arrancandoRef.current = true;
    canceladoRef.current = false;
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (canceladoRef.current) { stream.getTracks().forEach((t) => t.stop()); arrancandoRef.current = false; return; }
      streamRef.current = stream;
      chunksRef.current = [];
      const tipos = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus", "audio/ogg"];
      let mime = "";
      if (window.MediaRecorder && MediaRecorder.isTypeSupported) {
        mime = tipos.find((t) => MediaRecorder.isTypeSupported(t)) || "";
      }
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const tipo = mr.mimeType || mime || "audio/webm";
        const b = new Blob(chunksRef.current, { type: tipo });
        setBlob(b);
        setUrl(URL.createObjectURL(b));
        setEstado("listo");
        limpiarMedios();
      };
      mrRef.current = mr;
      mr.start(); // sin timeslice: un solo flush confiable al parar

      // Analizador de volumen para la onda
      try {
        const ac = new (window.AudioContext || window.webkitAudioContext)();
        acRef.current = ac;
        const src = ac.createMediaStreamSource(stream);
        const analyser = ac.createAnalyser();
        analyser.fftSize = 256;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const loop = () => {
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
          setNivel(Math.min(1, Math.sqrt(sum / buf.length) * 3));
          rafRef.current = requestAnimationFrame(loop);
        };
        loop();
      } catch {}

      setSegundos(0);
      setEstado("grabando");
      timerRef.current = setInterval(() => setSegundos((s) => s + 1), 1000);
    } catch (e) {
      setError("No pude prender el micrófono. Fijate que el navegador tenga permiso (candado en la barra de direcciones → Micrófono → Permitir).");
      limpiarMedios();
    } finally {
      arrancandoRef.current = false;
    }
  }, [estado, limpiarMedios]);

  const stop = useCallback(() => {
    canceladoRef.current = true;
    if (mrRef.current && mrRef.current.state !== "inactive") {
      mrRef.current.stop();
    } else {
      limpiarMedios();
    }
  }, [limpiarMedios]);

  const toggle = useCallback(() => {
    if (estado === "grabando") stop();
    else start();
  }, [estado, start, stop]);

  const reset = useCallback(() => {
    if (url) URL.revokeObjectURL(url);
    setBlob(null); setUrl(null); setSegundos(0); setNivel(0); setEstado("idle"); setError(null);
  }, [url]);

  useEffect(() => () => { limpiarMedios(); if (url) URL.revokeObjectURL(url); }, [limpiarMedios, url]);

  const mmss = `${String(Math.floor(segundos / 60)).padStart(2, "0")}:${String(segundos % 60).padStart(2, "0")}`;

  return { estado, segundos, mmss, nivel, blob, url, error, toggle, start, stop, reset };
}
